import { Forbidden, Header, Menu } from '@/components';
import type { IMenu, IUserInfo } from '@/services/common';
import { getMenus, getUserInfo } from '@/services/common';
import { redirectToSignIn } from '@/utils';
import type { MenuDataItem } from '@ant-design/pro-components';
import type { AxiosRequestConfig, AxiosResponse } from '@umijs/max';
import { QueryClient, defineApp, history } from '@umijs/max';
import { message } from 'antd';
import Cookies from 'js-cookie';
import { setupSw } from './setupSw';

interface MenuDataItemWithSource extends MenuDataItem {
  source?: 'SERVER' | 'CLIENT';
}

setupSw();

export const getInitialState = async (): Promise<{
  userInfo: IUserInfo & { first_login?: boolean };
  menus: IMenu[];
} | null> => {
  if (history.location.pathname === SIGN_IN_PATH) {
    return null;
  }

  try {
    const [userInfo, menus] = await Promise.all([
      getUserInfo().then((res) => res.data),
      getMenus().then((res) => res.data),
    ]);

    return {
      userInfo,
      menus,
    };
  } catch (error) {
    return null;
  }
};

const _global = window as Window & typeof globalThis & { _token: string };

const getTokenFromSearchParams = () => {
  const searchParams = new URLSearchParams(location.search.split('?')[1]);
  return searchParams.get('token') || '';
};

export default defineApp({
  request: {
    // 10分钟
    timeout: 600000,
    baseURL: '/v4',
    headers: {
      Authorization:
        Cookies.get(TOKEN_NAME) ||
        _global._token ||
        (_global._token = getTokenFromSearchParams()),
    },
    requestInterceptors: [
      [
        (url, options) => {
          const token = Cookies.get(TOKEN_NAME);
          if (
            options.headers &&
            token &&
            token !== options.headers.Authorization
          ) {
            options.headers.Authorization = token;
          }
          return { url, options };
        },
        (error: Error) => Promise.reject(error),
      ],
    ],
    responseInterceptors: [
      // @ts-ignore
      [
        (response) => {
          const data = (response as AxiosResponse<IRes<any>>).data;
          const ignoreError = (
            response.config as AxiosRequestConfig & { ignoreError: boolean }
          ).ignoreError;
          if (!Object.hasOwn(data, 'code')) {
            return response;
          }
          switch (data.code) {
            case 0: {
              return response;
            }
            case 3003: {
              history.replace('/workbench');
              return;
            }
            default: {
              if (!ignoreError) {
                message.error(data.msg);
              }
              return Promise.reject(data);
            }
          }
        },
        (error) => {
          const response = (
            error as Error & { response?: AxiosResponse<IRes<unknown>> }
          ).response;

          if (response) {
            const { status } = response;

            switch (status) {
              case 401: {
                redirectToSignIn();
                break;
              }
              default: {
                if (ENV === 'qa') {
                  // ! 先只在qa试行下
                  // ? 上报接口错误至k8s，以实现一定程度的日志持久化，也为了我们能更熟悉k8s的使用
                  const { code, msg } = response.data;

                  navigator.sendBeacon(`/logs/${status}/${code}/${msg}`);
                }
              }
            }
          }
          return Promise.reject(error);
        },
      ],
    ],
  },
  reactQuery: {
    queryClient: new QueryClient(),
  },
  layout: ({ initialState }) => ({
    layout: 'mix',
    fixedHeader: true,
    fixSiderbar: true,
    disableMobile: true,
    unAccessible: <Forbidden />,
    menu: {
      params: {
        userUid: initialState?.userInfo?.ou_uid,
      },
      request: async (_, menuData) => {
        if (initialState) {
          // TODO 菜单改造

          /**
           * 后端改造菜单为树形结构从而废除前端菜单，前端菜单仅做路由注册用
           */

          const menuMap = menuData.reduce<Map<string, MenuDataItemWithSource>>(
            (menus, menu) => {
              menus.set(menu.path!, menu);

              return menus;
            },
            new Map(),
          );

          for (const menu of initialState.menus) {
            const { is_hide, url, menu_name, icon } = menu;

            if (is_hide) {
              continue;
            }

            const cache = menuMap.get(url);

            if (cache) {
              cache.name = menu_name;
              cache.path = url;
              cache.icon = icon;
              cache.source = 'SERVER';
            } else {
              menuData.push({
                name: menu_name,
                path: url,
                icon,
                source: 'SERVER',
              });
            }
          }
        }

        return menuData;
      },
    },
    headerRender: (props) => <Header {...props} />,
    menuRender: (props) => <Menu {...props} />,
  }),
});
